/**
 * Question Interceptor Hook
 *
 * Detects if the current question should be intercepted and replaced
 * with a custom flow (e.g., labs order, insurance verification).
 */

import { useMemo } from 'react';
import { useQuestionnaireContext } from '../context/QuestionnaireContext';
import { INTERCEPTOR_REGISTRY } from '../interceptors/registry';
import { InterceptorContext, QuestionInterceptor } from '../interceptors/types';
import { Question, Interstitial } from '../components/types';

export function useQuestionInterceptor(currentQuestion: Question | Interstitial | undefined) {
  const { state } = useQuestionnaireContext();

  return useMemo(() => {
    // Only intercept questions (not interstitials)
    if (!currentQuestion || currentQuestion.questionnaireItemKind !== 'question') {
      return null;
    }

    // Find matching interceptor in registry
    const interceptor = INTERCEPTOR_REGISTRY.find(
      (int) => int.questionSlug === currentQuestion.slug
    );

    if (!interceptor) {
      return null;
    }

    console.log('[useQuestionInterceptor] Found interceptor for question:', currentQuestion.slug);

    // Build context for interceptor
    const context: InterceptorContext = {
      questionSlug: currentQuestion.slug,
      questionnaireId: state.questionnaire?.id || '',
      responseId: state.responseId || '',
    };

    // Check if this question should be intercepted
    if (!interceptor.shouldIntercept(context)) {
      return null;
    }

    return {
      interceptor,
      context,
    };
  }, [currentQuestion, state.questionnaire?.id, state.responseId]);
}
